import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Chip, Avatar, Divider, Tooltip, Stack, Badge,
  TextField, InputAdornment, IconButton, Select, MenuItem,
  FormControl, TablePagination, LinearProgress, Paper
} from '@mui/material';
import {
  RefreshOutlined as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ThermostatOutlined as HeatIcon,
  PersonOutlined as PersonIcon,
  BarChartOutlined as BarIcon,
  AccessTimeOutlined as TimeIcon,
} from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

/* ─── Tokens ────────────────────────────────────────────────── */
const C = {
  navy:     '#0D1F3C',
  navyMid:  '#1A3460',
  blue:     '#2563EB',
  blueSoft: '#EFF6FF',
  green:    '#16A34A',
  greenSoft:'#F0FDF4',
  amber:    '#D97706',
  amberSoft:'#FFFBEB',
  red:      '#DC2626',
  redSoft:  '#FEF2F2',
  border:   '#E2E8F0',
  surface:  '#F8FAFD',
  white:    '#FFFFFF',
  text:     '#1E293B',
  muted:    '#64748B',
};

const DAYS    = ['monday','tuesday','wednesday','thursday','friday','saturday'];
const DAY_LBL = ['Mon','Tue','Wed','Thu','Fri','Sat'];

/* heat colour — 6-step green ramp */
const heatColor = (val, max) => {
  if (!val || val === 0) return { bg: '#F1F5F9', text: '#CBD5E1', border: '#E2E8F0' };
  const pct = val / (max || 8);
  if (pct >= 0.85) return { bg: '#14532D', text: '#fff',     border: '#14532D' };
  if (pct >= 0.65) return { bg: '#166534', text: '#fff',     border: '#166534' };
  if (pct >= 0.45) return { bg: '#16A34A', text: '#fff',     border: '#15803D' };
  if (pct >= 0.25) return { bg: '#4ADE80', text: '#14532D',  border: '#22C55E' };
  return              { bg: '#DCFCE7', text: '#166534',  border: '#BBF7D0' };
};

const bandCfg = (band) => ({
  Healthy:    { bg: C.greenSoft, color: C.green,  dot: '#22C55E' },
  Overloaded: { bg: C.redSoft,   color: C.red,    dot: '#EF4444' },
  Underloaded:{ bg: C.amberSoft, color: C.amber,  dot: '#F59E0B' },
}[band] || { bg: '#F1F5F9', color: C.muted, dot: '#94A3B8' });

/* utilisation bar colour */
const utilColor = (pct) => {
  if (pct >= 80) return '#16A34A';
  if (pct >= 40) return '#D97706';
  return '#DC2626';
};

/* consistent avatar hue per teacher */
const avatarHue = (id) => `hsl(${(id * 53) % 360}, 55%, 38%)`;
const avatarBg  = (id) => `hsl(${(id * 53) % 360}, 60%, 92%)`;

const fmtPct = (v) => `${(v ?? 0).toFixed(1)}%`;

const getDefaultFromDate = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d.toISOString().split('T')[0];
};

const getDefaultToDate = () => getSaturdayOfWeek(getDefaultFromDate());

const getSaturdayOfWeek = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday + 5); // Monday + 5 = Saturday
  return d.toISOString().split('T')[0];
};

/* ─── Component ─────────────────────────────────────────────── */
export default function TeacherLoadHeatmap() {
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
const [toDate,   setToDate]   = useState(getDefaultToDate());
  const [heatmap,     setHeatmap]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [bandFilter,  setBandFilter]  = useState('All');
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Branch state (mirrors Employee.js pattern) ──────────────────────────
  const [isMaster,         setIsMaster]         = useState(false);
  const [branches,         setBranches]         = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // ── Resolve active branchId for use in all API calls ──────────────────
  // Master users: from dropdown selection
  // Non-master users: from sessionStorage (set at login)
  const activeBranchId = isMaster
    ? selectedBranchId
    : sessionStorage.getItem('branchId');

  // ── Fetch branch list (master users only) ─────────────────────────────
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        // Auto-fill first branch for master user when branches load
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
  if (fromDate) {
    setToDate(getSaturdayOfWeek(fromDate));
  }
}, [fromDate]);

  // ── On mount: detect master user, load branches or read from session ──
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      // fetchBranches sets selectedBranchId → triggers data load via selectedBranchId effect
      fetchBranches();
    } else {
      // Non-master: branchId already in session, activeBranchId resolves it directly
    }
  }, []);

  // ── Re-fetch heatmap whenever branch changes (or on initial mount for non-master) ──
  // Guard: don't fetch until we have a valid branchId
 useEffect(() => {
    if (!activeBranchId) return;
    fetchData();
  }, [selectedBranchId, fromDate, toDate]); // selectedBranchId change covers master; non-master fires once on mount via activeBranchId

  // ── Fetch heatmap data ────────────────────────────────────────────────
  // API integrated on this page:
  //   GET /adv/teacher-load/heatmap?Branch_id={branchId}
  // Branch_id is passed as a query parameter (integer $int32, query) per Swagger spec.
 const fetchData = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token  = sessionStorage.getItem('token');
      const params = new URLSearchParams({ Branch_id: activeBranchId });
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate)   params.set('toDate',   toDate);
      const res    = await fetch(`${apiUrl}/adv/teacher-load/heatmap?${params}`, {
        headers: { Authorization: token },
      });
      setHeatmap(await res.json().then(d => Array.isArray(d) ? d : []));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
};

  const maxPeriods = useMemo(
    () => Math.max(...heatmap.map(t => Math.max(...DAYS.map(d => t[d] || 0))), 1),
    [heatmap]
  );

  /* totals row */
  const totalByDay = useMemo(() =>
    DAYS.reduce((acc, day) => {
      acc[day] = heatmap.reduce((s, t) => s + (t[day] || 0), 0);
      return acc;
    }, {}),
  [heatmap]);

  /* summary stats */
  const stats = useMemo(() => {
    const total     = heatmap.length;
    const healthy   = heatmap.filter(t => t.loadBand === 'Healthy').length;
    const over      = heatmap.filter(t => t.loadBand === 'Overloaded').length;
    const under     = heatmap.filter(t => t.loadBand === 'Underloaded').length;
    const avgUtil   = total ? (heatmap.reduce((s, t) => s + (t.utilizationPercent || 0), 0) / total).toFixed(1) : 0;
    return { total, healthy, over, under, avgUtil };
  }, [heatmap]);

  /* filter + search */
  const filtered = useMemo(() => {
    let list = heatmap;
    if (bandFilter !== 'All') list = list.filter(t => t.loadBand === bandFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => (t.teacherName || '').toLowerCase().includes(q));
    }
    return list;
  }, [heatmap, search, bandFilter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  /* ── render ── */
  return (
    <Box sx={{ bgcolor: C.surface, minHeight: '100vh', pb: 5, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      {/* ── Hero ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 60%, #1e4d8c 100%)`,
        pt: 4, pb: 3.5, px: 4, position: 'relative', overflow: 'hidden',
      }}>
        {[{ s: 220, r: -40, t: -60, o: 0.06 }, { s: 120, r: 140, t: 30, o: 0.04 }].map((c, i) => (
          <Box key={i} sx={{
            position: 'absolute', right: c.r, top: c.t, width: c.s, height: c.s,
            borderRadius: '50%', border: `2px solid rgba(255,255,255,${c.o * 5})`,
            bgcolor: `rgba(255,255,255,${c.o})`, pointerEvents: 'none',
          }} />
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, p: 1, display: 'flex' }}>
                <HeatIcon sx={{ color: '#93C5FD', fontSize: 22 }} />
              </Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.5px' }}>
                Teacher Load Heatmap
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', ml: 6.5 }}>
              Day-wise period distribution and utilisation per teacher
            </Typography>
          </Box>

          {/* Right: Branch Dropdown (master users only) + Refresh button */}
          <Stack direction="row" alignItems="center" spacing={1.5}>

            {/* ── Branch Dropdown — visible only for Master/Admin users ── */}
            {isMaster && (
              <FormControl size="small" sx={{ width: 220 }}>
                <Select
                  value={selectedBranchId || ''}
                  onChange={(e) => {
                    setSelectedBranchId(e.target.value);
                    // Reset search, band filter and pagination when branch changes
                    setSearch('');
                    setBandFilter('All');
                    setPage(0);
                  }}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                    </InputAdornment>
                  }
                  renderValue={(val) => {
                    if (!val) return <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Select Branch</Typography>;
                    const found = branches.find(b => b.branchId === val);
                    return <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}>{found?.branchName || val}</Typography>;
                  }}
                  sx={{
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    letterSpacing: '0.3px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.15)',
                      borderWidth: '1px',
                      transition: 'border-color 0.2s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.11)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.35)',
                      },
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#64B5F6',
                        borderWidth: '1.5px',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255,255,255,0.5)',
                      transition: 'color 0.2s ease',
                    },
                    '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                    '& .MuiSelect-select': { py: 1.1 },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid rgba(11,31,61,0.12)',
                        boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                        overflow: 'hidden',
                        '& .MuiList-root': { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#0B1F3D',
                        px: 2, py: 1.1, mx: 0.5,
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25,118,210,0.1)',
                          color: '#1565C0',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Refresh button — respects current branch via activeBranchId in fetchData() */}
           {/* ── Date Range Filter ── */}
          <TextField
            label="From" type="date" size="small"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)',
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              '& input': { color: '#fff' },
            }}
          />
         <TextField
            label="To" type="date" size="small"
            value={toDate}
            disabled
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)',
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                '&.Mui-disabled fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              '& .MuiInputLabel-root.Mui-disabled': { color: 'rgba(255,255,255,0.6)' },
              '& input': { color: '#fff', WebkitTextFillColor: '#fff' },
              '& .MuiInputBase-input.Mui-disabled': { color: '#fff', WebkitTextFillColor: '#fff' },
            }}
          />

          {/* Refresh button — respects current branch via activeBranchId in fetchData() */}
          <Tooltip title="Refresh data">
              <Box onClick={fetchData} sx={{
                cursor: 'pointer', color: '#fff', bgcolor: 'rgba(255,255,255,0.12)',
                borderRadius: '50%', width: 42, height: 42,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.22)', transform: 'rotate(30deg)' },
              }}>
                <RefreshIcon />
              </Box>
            </Tooltip>
          </Stack>
        </Box>

        {/* stat pills */}
        {!loading && (
          <Stack direction="row" spacing={2} mt={2.5} flexWrap="wrap">
            {[
              { label: 'Total Teachers', value: stats.total,    icon: '👥' },
              { label: 'Healthy',        value: stats.healthy,  icon: '✅' },
              { label: 'Overloaded',     value: stats.over,     icon: '🔴' },
              { label: 'Underloaded',    value: stats.under,    icon: '🟡' },
              { label: 'Avg Utilisation',value: `${stats.avgUtil}%`, icon: '📊' },
            ].map(s => (
              <Box key={s.label} sx={{
                bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, px: 2, py: 1,
                backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)',
              }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.67rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {s.icon} {s.label}
                </Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.15rem', fontWeight: 800, lineHeight: 1.3 }}>
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>

        {/* ── Legend ── */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.07)', border: `1px solid ${C.border}`, mb: 3 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.navy, mb: 1.5 }}>
              Colour Legend — Periods per Day
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
              {[
                { label: '0 Periods',  bg: '#F1F5F9', border: '#E2E8F0' },
                { label: '1–2',        bg: '#DCFCE7', border: '#BBF7D0' },
                { label: '3–4',        bg: '#4ADE80', border: '#22C55E' },
                { label: '5–6',        bg: '#16A34A', border: '#15803D' },
                { label: '7',          bg: '#166534', border: '#166534' },
                { label: '8+',         bg: '#14532D', border: '#14532D' },
              ].map((item, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={0.7}>
                  <Box sx={{ width: 28, height: 22, borderRadius: 1, bgcolor: item.bg, border: `1px solid ${item.border}` }} />
                  <Typography variant="caption" sx={{ fontWeight: 500, color: C.muted }}>{item.label}</Typography>
                </Stack>
              ))}
              <Box sx={{ ml: 'auto' }} />
              {['Healthy', 'Overloaded', 'Underloaded'].map(b => {
                const cfg = bandCfg(b);
                return (
                  <Stack key={b} direction="row" alignItems="center" spacing={0.7}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.dot }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: cfg.color }}>{b}</Typography>
                  </Stack>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        {/* ── Main Table Card ── */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.07)', border: `1px solid ${C.border}` }}>

          {/* toolbar */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center', p: 2.5, pb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: C.navy }}>
                Weekly Load Overview
                <Badge badgeContent={filtered.length} color="primary" sx={{ ml: 2, '& .MuiBadge-badge': { fontSize: '0.65rem', fontWeight: 800 } }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {filtered.length !== heatmap.length
                  ? `Showing ${filtered.length} of ${heatmap.length} teachers`
                  : `${heatmap.length} teachers — darker = higher load`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              {/* search */}
              <TextField
                size="small" placeholder="Search teacher…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.muted }} /></InputAdornment>,
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {/* band filter */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={bandFilter}
                  onChange={e => { setBandFilter(e.target.value); setPage(0); }}
                  displayEmpty
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="All">All Bands</MenuItem>
                  <MenuItem value="Healthy">Healthy</MenuItem>
                  <MenuItem value="Overloaded">Overloaded</MenuItem>
                  <MenuItem value="Underloaded">Underloaded</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Divider />

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 8, gap: 2 }}>
              <CircularProgress sx={{ color: C.navy }} thickness={4} />
              <Typography color="text.secondary" variant="body2">Loading heatmap data…</Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>🔍</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: C.navy }}>No teachers found</Typography>
              <Typography color="text.secondary" variant="body2">Try adjusting your search or filter.</Typography>
            </Box>
          ) : (
            <>
              {/* scrollable table with fixed height */}
              <TableContainer sx={{ maxHeight: 520, overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 6, height: 6 },
                '&::-webkit-scrollbar-track': { bgcolor: '#F1F5F9' },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: 3 },
              }}>
                <Table stickyHeader sx={{
                  '& .MuiTableCell-root': { px: 1.5, py: 1 },
                  '& .MuiTableRow-root:hover td': { bgcolor: C.blueSoft },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 50 }}>#</TableCell>
                      <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 170 }}>Teacher</TableCell>
                      {DAY_LBL.map((d, i) => (
                        <TableCell key={d} align="center" sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 64 }}>
                          {d}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 72 }}>Weekly</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 70 }}>Max</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 70 }}>Ideal</TableCell>
                      <TableCell sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 130 }}>Utilisation</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 110 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginated.map((t, idx) => {
                      const cfg  = bandCfg(t.loadBand);
                      const util = t.utilizationPercent ?? 0;
                      return (
                        <TableRow key={t.teacherId} sx={{ '&:last-child td': { border: 0 } }}>

                          {/* # */}
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: C.muted, fontSize: '0.78rem' }}>
                              {page * rowsPerPage + idx + 1}
                            </Typography>
                          </TableCell>

                          {/* teacher */}
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: avatarBg(t.teacherId), color: avatarHue(t.teacherId), fontSize: '0.78rem', fontWeight: 800, flexShrink: 0 }}>
                                {(t.teacherName || '?').trim().charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
                                  {t.teacherName?.trim()}
                                </Typography>
                          {/* <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
  ID {t.teacherId}
</Typography> */}
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* day heat cells */}
                          {DAYS.map(day => {
                            const val  = t[day] || 0;
                            const clr  = heatColor(val, maxPeriods);
                            return (
                              <TableCell key={day} align="center" sx={{ p: '4px 6px' }}>
                                <Tooltip title={`${val} period${val !== 1 ? 's' : ''} on ${day.charAt(0).toUpperCase() + day.slice(1)}`} arrow>
                                  <Box sx={{
                                    width: 42, height: 32, borderRadius: 1.5, mx: 'auto',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: clr.bg, border: `1px solid ${clr.border}`,
                                    transition: 'transform 0.15s, box-shadow 0.15s',
                                    cursor: 'default',
                                    '&:hover': { transform: 'scale(1.15)', boxShadow: `0 2px 8px ${clr.bg}80` },
                                  }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: clr.text, lineHeight: 1 }}>
                                      {val || '–'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            );
                          })}

                          {/* weekly total */}
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 800, color: C.navy, fontSize: '0.9rem' }}>
                              {t.weeklyLoad ?? 0}
                            </Typography>
                          </TableCell>

                          {/* max */}
                          <TableCell align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t.maxPeriods ?? 45}
                            </Typography>
                          </TableCell>

                          {/* ideal */}
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: C.muted }}>
                              {t.idealLoad ?? '—'}
                            </Typography>
                          </TableCell>

                          {/* utilisation bar */}
                          <TableCell sx={{ pr: 2 }}>
                            <Stack spacing={0.4}>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" sx={{ fontWeight: 700, color: utilColor(util) }}>
                                  {fmtPct(util)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: C.muted, fontSize: '0.65rem' }}>
                                  gap {t.idealGap ?? 0}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(util, 100)}
                                sx={{
                                  height: 6, borderRadius: 3, bgcolor: '#EEF2F7',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 3,
                                    bgcolor: utilColor(util),
                                  },
                                }}
                              />
                            </Stack>
                          </TableCell>

                          {/* band chip */}
                          <TableCell align="center">
                            <Chip
                              label={t.loadBand || 'Normal'}
                              size="small"
                              sx={{
                                bgcolor: cfg.bg, color: cfg.color,
                                fontWeight: 700, fontSize: '0.7rem', borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Daily Totals sticky footer row */}
                    <TableRow sx={{ bgcolor: '#F8FAFC', position: 'sticky', bottom: 0, zIndex: 1 }}>
                      <TableCell />
                      <TableCell sx={{ fontWeight: 700, color: C.navy, fontSize: '0.8rem' }}>
                        📊 Daily Totals
                      </TableCell>
                      {DAYS.map(day => (
                        <TableCell key={day} align="center">
                          <Typography variant="body2" sx={{ fontWeight: 800, color: C.navy }}>
                            {totalByDay[day]}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: C.navy }}>
                          {Object.values(totalByDay).reduce((a, b) => a + b, 0)}
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={4} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              {/* pagination */}
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
                sx={{
                  '& .MuiTablePagination-toolbar': { px: 2 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontWeight: 600, fontSize: '0.8rem',
                  },
                }}
              />
            </>
          )}
        </Card>
      </Container>
    </Box>
  );
}